import { useCallback, useEffect, useState } from 'react'
import type { SiteSummary } from './types'
import { api, ApiError } from '../api/client'
import { getRuntime } from './_mode'
import { captureHookFailure, recordHookOutcome } from '../observability/sentry'

function normalizeSite(row: {
  id: string
  project_id: string
  owner_user_id: string
  name: string
  slug: string
  domain: string | null
  status: string
  updated_at: string
}): SiteSummary {
  return {
    id: row.id,
    project_id: row.project_id,
    owner_user_id: row.owner_user_id,
    name: row.name,
    slug: row.slug,
    domain: row.domain,
    status: row.status,
    updated_at: row.updated_at,
  }
}

export function useSite(projectId: string | undefined, projectName?: string) {
  const [site, setSite] = useState<SiteSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!projectId) {
      setSite(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    const { live } = getRuntime()
    if (!live) {
      setSite(null)
      setLoading(false)
      recordHookOutcome('useSite', 'demo_fallback', {
        route: '/api/site/sites',
        rows: 0,
      })
      return
    }

    const startedAt = Date.now()
    try {
      const result = await api.site.get(projectId)
      setSite(result.site ? normalizeSite(result.site) : null)
      recordHookOutcome('useSite', 'live_success', {
        route: '/api/site/sites',
        rows: result.site ? 1 : 0,
        durationMs: Date.now() - startedAt,
      })
    } catch (err) {
      const apiErr = err as ApiError
      setSite(null)
      setError(apiErr?.message || 'site_lookup_failed')
      captureHookFailure('useSite', 'live_error', err, {
        route: '/api/site/sites',
        code: apiErr?.code,
        status: apiErr?.status,
        durationMs: Date.now() - startedAt,
      })
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    void load()
  }, [load])

  const initialize = useCallback(async () => {
    if (!projectId || !projectName) return null
    const { live } = getRuntime()
    if (!live) {
      throw new ApiError(503, 'demo_mode', 'site initialization is disabled in demo mode')
    }
    setCreating(true)
    setError(null)
    try {
      const result = await api.site.create({ projectId, projectName })
      const next = normalizeSite(result.site)
      setSite(next)
      return next
    } catch (err) {
      const apiErr = err as ApiError
      setError(apiErr?.message || 'site_create_failed')
      return null
    } finally {
      setCreating(false)
    }
  }, [projectId, projectName])

  return {
    site,
    loading,
    creating,
    error,
    live: getRuntime().live,
    initialize,
    refresh: load,
  }
}
